import type {
  ECKMConditionsData,
  CKMConditionsData,
  BehavioralHealthConditionsData,
  BehavioralHealthScreenerData,
  EligibilityResult,
  EligibilityTrackResult,
  ACCESSEligibilityResultCode,
  TrackType,
} from '@/types/registration';
import { meetsBHThreshold } from '@/utils/bhScreener';

// ─── CMS eligibility-result helpers (RND-36523 eligibility-first reorder) ───

/**
 * Result codes that count as "CMS-eligible" for a track. Mirrors
 * cms-integration-service src/fhir/valuesets/eligibilityResultVS.ts. Shared with
 * EligibilityResultStep so both consumers agree on a single definition.
 */
export const ELIGIBLE_CODES = new Set<ACCESSEligibilityResultCode>([
  'eligible',
  'eligible-pending-diagnosis',
  'eligible-switch-participants',
]);

/**
 * Tracks the CMS $check-eligibility API confirmed as eligible, derived from the
 * per-track results. Only complete results carrying an eligible result code
 * count; processing / error / not-eligible are excluded. This is the source of
 * truth that drives the eligibility-first flow — which health-condition screens
 * and screeners are shown, and the early exits (RND-36523).
 */
export const getCmsEligibleTracks = (
  results: EligibilityTrackResult[] = []
): TrackType[] =>
  results
    .filter(r => r.status === 'complete' && r.resultCode && ELIGIBLE_CODES.has(r.resultCode))
    .map(r => r.track);

/**
 * Route of the first health-conditions screen to show for a set of CMS-eligible
 * tracks, in eCKM > CKM > BH priority. Used by EligibilityResultStep to enter
 * the filtered questionnaire. '/not-eligible' is a defensive fallback — callers
 * only invoke this with a non-empty eligible set.
 */
export const firstConditionRouteForTracks = (tracks: TrackType[]): string => {
  if (tracks.includes('eCKM')) return '/health-conditions';
  if (tracks.includes('CKM')) return '/ckm-conditions';
  if (tracks.includes('BH')) return '/behavioral-health-conditions';
  return '/not-eligible';
};

/**
 * Determines if user is eligible for eCKM track
 * Rules:
 * - Hypertension (high blood pressure) OR
 * - Any 2+ of (High cholesterol, Prediabetes, Overweight)
 */
export const isEligibleForECKM = (conditions: ECKMConditionsData): boolean => {
  const selected = conditions.selectedConditions;

  // If no conditions selected, not eligible
  if (selected.size === 0) return false;

  // Check hypertension first
  if (selected.has('highBloodPressure')) return true;

  // Count risk factors (excluding hypertension)
  const riskFactors = ['highCholesterol', 'prediabetes', 'overweightOrObesity'] as const;
  const riskFactorCount = riskFactors.filter(factor => selected.has(factor)).length;

  return riskFactorCount >= 2;
};

/**
 * Determines if user has CKM exclusion conditions
 * These conditions exclude user from eCKM track
 */
export const hasCKMExclusions = (conditions: CKMConditionsData): boolean => {
  return conditions.selectedConditions.size > 0;
};

/**
 * Determines if user is eligible for CKM track
 * Rules: Any of (Diabetes, Chronic kidney disease, Heart disease)
 */
export const isEligibleForCKM = (conditions: CKMConditionsData): boolean => {
  return hasCKMExclusions(conditions);
};

/**
 * Determines if user is eligible for Behavioral Health track.
 *
 * Rules: self-reports depression OR anxiety, AND — once the PHQ-9/GAD-7 screener
 * has been taken — meets the BH threshold (PHQ-9 >= 10 OR GAD-7 >= 10).
 *
 * PRD 01 v0.7 §6.4: a below-threshold screener drops the BH track. Before the
 * screener has run (scores still null) the self-reported condition alone keeps
 * BH a candidate, so the flow proceeds to the screener rather than dropping it
 * prematurely. Kept a pure predicate (no flow-position coupling) so it survives
 * the §6.2 eligibility-first reorder (RND-36523).
 *
 * The still-open ICD-10 override (PRD §9 id-10, Edo + Jessica) slots in here
 * additively as `|| icd10Override(...)` once that decision lands — it can only
 * re-include BH, never undo this drop.
 */
export const isEligibleForBH = (
  conditions: BehavioralHealthConditionsData,
  screener?: BehavioralHealthScreenerData | null
): boolean => {
  if (conditions.selectedConditions.size === 0) return false;

  // Pre-screener (scores not both present): the self-reported condition alone
  // keeps BH a candidate so the flow proceeds to the PHQ-9/GAD-7 screener.
  const phq9Score = screener?.phq9Score ?? null;
  const gad7Score = screener?.gad7Score ?? null;
  if (phq9Score === null || gad7Score === null) return true;

  return meetsBHThreshold(phq9Score, gad7Score);
};

// Soft fallback for the invariant that eCKM and CKM cannot both be eligible.
// The logic above prevents co-occurrence; this resolves it defensively if a future
// regression ever reaches here, so enrollment never hard-crashes.
// Priority: eCKM > CKM — drop CKM, keep eCKM.
export const resolveTrackConflict = (tracks: TrackType[]): TrackType[] => {
  if (tracks.includes('eCKM') && tracks.includes('CKM')) {
    console.error('Invariant violation: eCKM and CKM cannot both be eligible simultaneously');
    return tracks.filter(t => t !== 'CKM');
  }
  return tracks;
};

/**
 * Main eligibility determination function
 * Implements priority logic: eCKM > CKM > BH
 * BH is additive if eCKM or CKM eligible, standalone if not
 */
export const determineEligibility = (
  eCKM: ECKMConditionsData,
  ckm: CKMConditionsData,
  bh: BehavioralHealthConditionsData,
  bhScreener?: BehavioralHealthScreenerData | null
): EligibilityResult => {
  const eligibleTracks: TrackType[] = [];

  // Check eCKM eligibility (priority 1)
  const eCKMEligible = isEligibleForECKM(eCKM);
  const hasExclusions = hasCKMExclusions(ckm);

  if (eCKMEligible && !hasExclusions) {
    eligibleTracks.push('eCKM');
  }

  // Check CKM eligibility (priority 2, only if eCKM fails)
  const ckmEligible = isEligibleForCKM(ckm);
  if (ckmEligible && !eligibleTracks.includes('eCKM')) {
    eligibleTracks.push('CKM');
  }

  // Check BH eligibility (additive if eCKM or CKM eligible, standalone if not).
  // Once the screener has run, a below-threshold score drops BH here (§6.4).
  const bhEligible = isEligibleForBH(bh, bhScreener);
  if (bhEligible) {
    eligibleTracks.push('BH');
  }

  const resolvedTracks = resolveTrackConflict(eligibleTracks);
  return {
    eligibleTracks: resolvedTracks,
    isEligible: resolvedTracks.length > 0,
    primaryTrack: resolvedTracks[0] || null,
  };
};

/**
 * Determines the next step after eCKM conditions
 * Always go to CKM conditions step regardless of eCKM eligibility
 */
export const getNextStepAfterECKM = (): '/ckm-conditions' => {
  return '/ckm-conditions';
};

/**
 * Determines the next step after CKM conditions
 */
export const getNextStepAfterCKM = (): '/behavioral-health-conditions' => {
  // Always go to BH after CKM
  return '/behavioral-health-conditions';
};

/**
 * Determines the next step after BH conditions
 */
export const getNextStepAfterBH = (
  eligibility: EligibilityResult
): '/track-offering' | '/not-eligible' => {
  return eligibility.isEligible ? '/track-offering' : '/not-eligible';
};
